/**
 * Colour + formatting rules shared by every Log Review surface.
 *
 * The point of keeping these in one place is that a status has to read the
 * same colour wherever it appears — a "Pending Approval" slice in the
 * workflow donut and its badge in the table below it must not disagree.
 */

import type { AreaChartData, PieChartDatum } from "../../types/logReview";

/** Log Review status → rego StatusBadge variant. */
export type LogReviewStatusVariant =
  | "pending"
  | "rejected"
  | "approved"
  | "open"
  | "closed";

/**
 * Maps a Log Review status string onto a StatusBadge variant. `pending` is
 * checked FIRST so "Pending Approval" reads as amber and never matches the
 * `approv` branch below (which would paint it the same violet as "Approved").
 */
export const logReviewStatusVariant = (
  status: string | null | undefined,
): LogReviewStatusVariant => {
  const value = (status ?? "").toLowerCase();
  if (value.includes("pending")) return "pending";
  if (value.startsWith("non compliant") || value.includes("reject")) return "rejected";
  if (value.startsWith("compliant") || value.includes("approv")) return "approved";
  if (value.includes("open")) return "open";
  return "closed";
};

/**
 * Concrete colour per status, so status-driven charts read the SAME colour
 * as the table badges. Returns `null` for the unknown bucket, letting chart
 * callers fall back to their own categorical palette.
 */
export const logReviewStatusColor = (status: string | null | undefined): string | null => {
  const value = (status ?? "").toLowerCase().trim();
  // "Pending Review" and "Pending Approval" both map to the amber `pending`
  // variant, so they would collide in the workflow donut — give them distinct
  // hues so the two stages read apart.
  if (value.includes("pending approval")) return "#a855f7"; // purple
  // Sky, carried over from g360, where this status is stamped #38bdf8 on the
  // chart data itself. Distinct from the info-blue "Open" slice it sits beside
  // in the workflow donut: ΔE 15.9 for full colour vision, 16.3 under
  // deuteranopia, both clear of the floors.
  if (value.includes("pending review")) return "#38bdf8"; // sky
  // Compliance Status Overview buckets — every "non…compliant/compliance"
  // label is deterministically red, and the acknowledged variant splits off
  // to orange, so the two never share a colour regardless of hyphen /
  // spacing / spelling drift.
  if (value.startsWith("non")) {
    return value.includes("ack") ? "#f97316" /* orange */ : "#ef4444" /* red */;
  }
  // A bare "Acknowledged" is the Log Reviewer queue's positive outcome — the
  // same place "Approved" sits in the CISO queue — so it reads the same green.
  // The two never share a chart, and the compliance-overview "Non-Compliant
  // (Acknowledged)" bucket is already resolved to orange above.
  if (value.includes("acknowledg")) return "#22c55e";
  switch (logReviewStatusVariant(status)) {
    case "approved":
      return "#22c55e";
    case "pending":
      return "#f59e0b";
    case "rejected":
      return "#ef4444";
    case "open":
      return "#3b82f6";
    default:
      return null;
  }
};

/** Saturated categorical fallback — keeps on-slice `%` labels readable. */
export const LOG_REVIEW_DONUT_PALETTE = [
  "#4f7cf5", // blue
  "#22c55e", // green
  "#f97316", // orange
  "#a855f7", // purple
  "#ef4444", // red
  "#14b8a6", // teal
  "#eab308", // amber
  "#ec4899", // pink
  "#64748b", // slate
  "#0ea5e9", // sky
];

/** Accent cycled per rank — apps/categories are dynamic, so key by position. */
export const RANK_COLORS = ["#ef4444", "#3b82f6", "#8b5cf6", "#14b8a6", "#f59e0b"];

/**
 * Saturated hues for the "by Team" treemap. The tiles are outlined rather
 * than filled, so this colour is the stroke, the glow AND the label — soft
 * tints built to sit behind dark text read as washed-out wire once the fill
 * is gone. Kept in step with the same list in `logReviewApi`.
 */
export const TEAM_HEATMAP_COLORS = [
  "#22c55e",
  "#8b5cf6",
  "#ef4444",
  "#f59e0b",
  "#06b6d4",
];

/**
 * Resolve one colour per donut slice, in data order: an explicit per-datum
 * `color` wins, then the status badge colour, then the categorical palette.
 */
export const logReviewDonutColors = (
  data: ReadonlyArray<{ name: string; color?: string }>,
): string[] =>
  data.map(
    (d, i) =>
      d.color ??
      logReviewStatusColor(d.name) ??
      LOG_REVIEW_DONUT_PALETTE[i % LOG_REVIEW_DONUT_PALETTE.length],
  );

// ─── Severity colours (distribution gauge + risk badges) ─────────────────

const SEVERITY_HEX: Record<string, string> = {
  Critical: "#EF4444",
  High: "#F97316",
  Medium: "#FFB84D",
  Low: "#00B8D4",
};

/** Severity label → hex. Unrecognised labels resolve to a neutral slate. */
export const severityColor = (label: string): string =>
  SEVERITY_HEX[label] ?? "#94A3B8";

/**
 * Risk label → StatusBadge variant for the table's Risk column. The return
 * type is narrowed to the badge's own severity vocabulary — the wider
 * {@link Severity} union carries an "unknown" bucket the badge has no style
 * for, so unrecognised risks fall back to `info` instead.
 */
export const riskVariant = (
  risk: string,
): "critical" | "high" | "medium" | "low" | "info" => {
  const s = (risk ?? "").toLowerCase();
  if (s.startsWith("critical")) return "critical";
  if (s.startsWith("high")) return "high";
  if (s.startsWith("medium")) return "medium";
  if (s.startsWith("low")) return "low";
  return "info";
};

// Number formatting, percentage shares, and the all-zero "treat as empty"
// check are not log-review concerns. Use `formatCompact` / `formatNumber` from
// ChartsComponent and `breakdownShares` from ChartBreakdownLegend; every shared
// chart already resolves its own empty state when the values sum to zero, so
// callers no longer pre-check.

/**
 * Reshape the service layer's `{ categories, series }` trend payload into the
 * row-per-x-value form the shared AreaChart consumes, plus the matching series
 * descriptors. Series names double as row keys, so the chart's legend, tooltip
 * and colour assignment all key off the same string.
 */
export const toAreaRows = (
  data: AreaChartData,
): {
  rows: Array<Record<string, string | number>>;
  series: Array<{ key: string; label: string; color?: string }>;
} => {
  const rows = data.categories.map((category, i) => {
    const row: Record<string, string | number> = { category };
    data.series.forEach((s) => {
      row[s.name] = s.data[i] ?? 0;
    });
    return row;
  });
  const series = data.series.map((s) => ({
    key: s.name,
    label: s.name,
    color: s.color,
  }));
  return { rows, series };
};

/**
 * Shared DonutPieChart configuration for the Log Review status rings.
 *
 * A props preset rather than a wrapper component: the shared DonutPieChart
 * already does everything these rings need, so the only thing worth naming
 * is the configuration the four call sites must agree on. Leader lines are
 * deliberate — the workflow ring routinely has 1-of-166 slivers that
 * "inside" labels would silently drop.
 */
export const LOG_REVIEW_DONUT_PROPS = {
  nameKey: "name",
  valueKey: "value",
  colorKey: "color",
  variant: "donut",
  height: 280,
  showLegend: true,
  legendPosition: "top",
  legendToggle: true,
  // Legend carries the label only. The share is already on each slice via the
  // leader labels below, and the tooltip has the count.
  showLegendShare: false,
  showSliceLabels: true,
  labelStyle: "leader",
  showCenterTotal: true,
  centerLabel: "Total",
  // Slices arrive status-ordered from the service layer; re-sorting would
  // break the fixed Non-Compliant → Ack → Compliant reading order.
  sort: "none",
} as const;

/**
 * Attach a resolved colour to each slice so the donut and its legend agree
 * even as slices are toggled off.
 */
export const withDonutColors = (data: PieChartDatum[]): PieChartDatum[] => {
  const colors = logReviewDonutColors(data);
  return data.map((d, i) => ({ ...d, color: colors[i] }));
};

/**
 * Colours for the Insights "Request Status" ring.
 *
 * Same resolution as {@link withDonutColors} with one deliberate difference:
 * "Pending Approval" is amber here rather than the purple
 * {@link logReviewStatusColor} gives it. That purple exists only to separate
 * the two pending stages where they appear together in the workflow donut.
 * This ring shows a single role's queue, so it carries exactly one pending
 * status and has nothing to be told apart from.
 *
 * "Pending Review" is deliberately NOT rewritten: its sky is the g360 colour,
 * and the reviewer's ring is one of the two places that carries it.
 */
export const withRequestStatusColors = (data: PieChartDatum[]): PieChartDatum[] =>
  withDonutColors(
    data.map((d) =>
      d.color == null && d.name.toLowerCase().includes("pending approval")
        ? { ...d, color: "#f59e0b" }
        : d,
    ),
  );
